import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { Role } from "../../../../features/authentication/enums/role.enum";
import { ResponseDto } from "../../../models/response-dto.model";
import { DashboardRange } from "../enums/dashboard-range.enum";
import { TenantUsageGraphData } from "../models/tenant-usage-graph-data.model";
import { TenantUsageWorkingGraphData } from "../models/tenant-usage-working-graph-data.model";
import { TenantUsage } from "../models/tenant-usage.model";
import { Paginate } from "../../../models/pagination.model";
import { TenantEdgeConsumption } from "../models/tenant-edge-consumption.model";

@Injectable({
  providedIn: "root"
})
export class DashboardService {
  private readonly http = inject(HttpClient);

  getDepartmentsCount(): Observable<number> {
    return this.http.get<ResponseDto<number>>(`/management-departments/count`).pipe(map((response) => response.data));
  }

  getOperatorsCount(tenant?: string): Observable<number> {
    let params = new HttpParams().set("role", Role.OPERATOR);

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    return this.http
      .get<ResponseDto<number>>(`/management-users/count`, { params })
      .pipe(map((response) => response.data));
  }

  getBundlesCount(): Observable<number> {
    return this.http.get<ResponseDto<number>>(`/management-bundles/count`).pipe(map((response) => response.data));
  }

  getTenantUsage(tenant?: string): Observable<TenantUsage> {
    let params = new HttpParams();
    params = params.set("timezoneOffset", new Date().getTimezoneOffset());

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    return this.http.get<ResponseDto<TenantUsage>>(`/tenant-usages`, { params }).pipe(map((response) => response.data));
  }

  getTenantUsageInteractions(range: DashboardRange | "all", tenant?: string): Observable<TenantUsageGraphData> {
    let params = new HttpParams().set("range", range);
    params = params.set("timezoneOffset", new Date().getTimezoneOffset());

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    return this.http
      .get<ResponseDto<TenantUsageGraphData>>(`/tenant-usages/interactions`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantUsageDocuments(
    range: DashboardRange | "all",
    tenant?: string,
    totalOnly?: boolean
  ): Observable<TenantUsageGraphData> {
    let params = new HttpParams().set("range", range);
    params = params.set("timezoneOffset", new Date().getTimezoneOffset());

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    if (totalOnly) {
      params = params.set("totalOnly", totalOnly);
    }

    return this.http
      .get<ResponseDto<TenantUsageGraphData>>(`/tenant-usages/documents`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantUsagePages(
    range: DashboardRange | "all",
    tenant?: string,
    totalOnly?: boolean
  ): Observable<TenantUsageGraphData> {
    let params = new HttpParams().set("range", range);
    params = params.set("timezoneOffset", new Date().getTimezoneOffset());

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    if (totalOnly) {
      params = params.set("totalOnly", totalOnly);
    }

    return this.http
      .get<ResponseDto<TenantUsageGraphData>>(`/tenant-usages/pages`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantUsageSummaries(
    range: DashboardRange | "all",
    tenant?: string,
    totalOnly?: boolean
  ): Observable<TenantUsageGraphData> {
    let params = new HttpParams().set("range", range);
    params = params.set("timezoneOffset", new Date().getTimezoneOffset());

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    if (totalOnly) {
      params = params.set("totalOnly", totalOnly);
    }

    return this.http
      .get<ResponseDto<TenantUsageGraphData>>(`/tenant-usages/summaries`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantUsageTopWorkingOperators(range: DashboardRange, tenant?: string): Observable<TenantUsageWorkingGraphData> {
    let params = new HttpParams().set("range", range);
    params = params.set("timezoneOffset", new Date().getTimezoneOffset());

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    return this.http
      .get<ResponseDto<TenantUsageWorkingGraphData>>(`/tenant-usages/top-working/operator`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantUsageTopWorkingTenants(range: DashboardRange, tenant?: string): Observable<TenantUsageWorkingGraphData> {
    let params = new HttpParams().set("range", range);
    params = params.set("timezoneOffset", new Date().getTimezoneOffset());

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    return this.http
      .get<ResponseDto<TenantUsageWorkingGraphData>>(`/tenant-usages/top-working/tenant-interactions`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantUsageOperators(
    tenant?: string,
    role?: Role
  ): Observable<number> {
    let params = new HttpParams();

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    if (role) {
      params = params.set("role", role);
    }

    return this.http
      .get<ResponseDto<number>>(`/management-users/count`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantUsageRepositories(
    tenant?: string
  ): Observable<number> {
    let params = new HttpParams();

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    return this.http
      .get<ResponseDto<number>>(`/management-departments/count`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantUsageBundles(
    tenant?: string
  ): Observable<number> {
    let params = new HttpParams();

    if (tenant) {
      params = params.set("tenant", tenant);
    }

    return this.http
      .get<ResponseDto<number>>(`/management-bundles/count`, { params })
      .pipe(map((response) => response.data));
  }

  getTenantEdges(
    page: number,
    pageSize: number
  ): Observable<Paginate<TenantEdgeConsumption>> {
    let params = new HttpParams();
    params = params.set("page", page);
    params = params.set("pageSize", pageSize);

    return this.http
      .get<ResponseDto<Paginate<TenantEdgeConsumption>>>(`/tenant-usages/edge/summaries`, { params })
      .pipe(map((response) => response.data));
  }
}
